import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  FieldValues,
  Path,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useStep, useUnmount } from 'usehooks-ts';
import type { ZodObject } from 'zod';

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

export interface MultiStepState {
  /**
   * The current 1-indexed step number.
   */
  current: number;
  /**
   * Navigate to a specific step (1-indexed). Out-of-range values are clamped
   * by the underlying step machine.
   */
  goToStep: (step: number) => void;
  /**
   * Navigate to the previous step. No-op on the first step.
   */
  handleBack: () => void;
  /**
   * Validate the current step's fields and advance if valid.
   * Returns `true` when the step advanced successfully.
   */
  handleNext: () => Promise<boolean>;
  /**
   * Whether the current step is the first step.
   */
  isFirst: boolean;
  /**
   * Whether the current step is the last step.
   */
  isLast: boolean;
  /**
   * Whether the form is currently running async validation as part of an
   * explicit `handleNext` call. Background revalidation from the watch
   * subscription does **not** set this flag.
   */
  isValidating: boolean;
  /**
   * Reset the step counter and clean up any active revalidation
   * subscription. Consumers should call this **before** calling the RHF
   * `reset()` to avoid the subscription re-triggering validation on the
   * newly cleared field values.
   */
  reset: () => void;
  /**
   * The total number of steps.
   */
  total: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Recovery callback invoked when `handleNext` fails validation. Returning
 * `true` signals "the recovery resolved the issue" — the schema is then re-checked and the user
 * only advances if the step now validates.
 */
export type OnValidationFailed<
  T extends FieldValues,
  TTransformedValues extends FieldValues = T,
> = (form: UseFormReturn<T, any, TTransformedValues>) => Promise<boolean>;

export interface UseMultiStepFormOptions<
  T extends FieldValues,
  TTransformedValues extends FieldValues = T,
> extends UseFormProps<T, any, TTransformedValues> {
  /**
   * Zod schemas for each step. The keys of each schema's `.shape` determine
   * which top-level fields are validated when advancing past that step.
   */
  stepSchemas: readonly ZodObject[];
  /**
   * Optional recovery hook invoked when `handleNext` fails validation.
   * See {@link OnValidationFailed}.
   */
  onValidationFailed?: OnValidationFailed<T, TTransformedValues>;
}

export interface UseMultiStepFormReturn<
  T extends FieldValues,
  TTransformedValues extends FieldValues = T,
> extends UseFormReturn<T, any, TTransformedValues> {
  /**
   * Step navigation state, isolated from the `useForm` return to avoid
   * naming conflicts.
   */
  steps: MultiStepState;
}

/**
 * Combines React Hook Form with step-based navigation and per-step Zod
 * validation.
 *
 * Wraps `useForm` and `useStep`, returning the full RHF API plus a `steps`
 * object for navigating between steps. Advancing to the next step triggers
 * validation of only the fields defined in that step's schema, keeping
 * later steps from blocking progress.
 *
 * Pair with {@link MultiStepFormProvider} to expose step state to consumer
 * descendants such as {@link MultiStepFormActions}. The optional second
 * generic carries the schema's post-transform output type so `handleSubmit`
 * yields the transformed values.
 */
export const useMultiStepForm = <
  T extends FieldValues,
  TTransformedValues extends FieldValues = T,
>({
  stepSchemas,
  onValidationFailed,
  ...formOptions
}: UseMultiStepFormOptions<T, TTransformedValues>): UseMultiStepFormReturn<
  T,
  TTransformedValues
> => {
  const total = stepSchemas.length;

  const [
    current,
    {
      canGoToPrevStep,
      canGoToNextStep,
      goToNextStep,
      goToPrevStep,
      reset: resetSteps,
      setStep,
    },
  ] = useStep(total);

  const form = useForm<T, any, TTransformedValues>(formOptions);

  const isFirst = !canGoToPrevStep;
  const isLast = !canGoToNextStep;

  // Managed separately from `formState.isValidating` so that background
  // revalidation triggered by the watch subscription (below) does not
  // flicker the Next button loading state. Only the explicit `handleNext`
  // call sets this to `true`.
  const [isValidating, setIsValidating] = useState(false);

  // ---------------------------------------------------------------------------
  // Watch subscription for per-step revalidation
  // ---------------------------------------------------------------------------
  //
  // `trigger()` validates fields on demand but does not set RHF's internal
  // `isSubmitted` flag, so the configured `reValidateMode` (e.g. 'onChange')
  // never activates for intermediate steps. This means errors surfaced by
  // `handleNext` won't clear as the user corrects them.
  //
  // To bridge that gap, when `handleNext` fails we subscribe to field value
  // changes via `form.watch()` and imperatively re-run validation on the
  // current step's fields for every change. The subscription is torn down
  // when the step advances, when the user navigates back, or when the step
  // counter is reset.
  // ---------------------------------------------------------------------------

  const watcherRef = useRef<{ unsubscribe: () => void } | null>(null);

  const clearWatcher = useCallback(() => {
    watcherRef.current?.unsubscribe();
    watcherRef.current = null;
  }, []);

  // Clean up the subscription on unmount.
  useUnmount(clearWatcher);

  const handleNext = useCallback(async () => {
    clearWatcher();
    setIsValidating(true);

    const stepSchema = stepSchemas[current - 1];
    const fieldNames = Object.keys(stepSchema.shape) as Path<T>[];

    // Silent pre-check via the step schema so any validation errors
    // don't show up in the UI: they may be fixed by the recovery hook
    // before the user ever sees them.
    const readStepValues = () =>
      Object.fromEntries(fieldNames.map(name => [name, form.getValues(name)]));
    let isValid = (await stepSchema.safeParseAsync(readStepValues())).success;

    if (!isValid && onValidationFailed) {
      // Recovery may only patch a subset of fields, so re-validate the
      // whole step after it runs. This prevents a partial repair from
      // silently bypassing unrelated validation.
      const recovered = await onValidationFailed(form);
      if (recovered) {
        isValid = (await stepSchema.safeParseAsync(readStepValues())).success;
      }
    }

    if (isValid) {
      setIsValidating(false);
      goToNextStep();
      return true;
    }

    // Recovery failed or wasn't configured — surface the inline errors
    // now via `form.trigger` so the user can correct the input.
    await form.trigger(fieldNames, { shouldFocus: true });
    setIsValidating(false);

    // Subscribe to individual field changes so errors clear as the user
    // corrects them. The `name` guard ensures only discrete field edits
    // trigger revalidation — bulk changes from `form.reset()` fire with
    // `name` undefined and are intentionally ignored to prevent stale
    // errors being re-created during a form reset.
    watcherRef.current = form.watch((_value, { name }) => {
      if (name) form.trigger(fieldNames);
    });

    return false;
  }, [
    clearWatcher,
    current,
    form,
    goToNextStep,
    onValidationFailed,
    stepSchemas,
  ]);

  const handleBack = useCallback(() => {
    clearWatcher();
    goToPrevStep();
  }, [clearWatcher, goToPrevStep]);

  const goToStep = useCallback(
    (step: number) => {
      clearWatcher();
      setStep(step);
    },
    [clearWatcher, setStep],
  );

  const reset = useCallback(() => {
    clearWatcher();
    setIsValidating(false);
    resetSteps();
  }, [clearWatcher, resetSteps]);

  const steps = useMemo<MultiStepState>(
    () => ({
      current,
      goToStep,
      handleBack,
      handleNext,
      isFirst,
      isLast,
      isValidating,
      reset,
      total,
    }),
    [
      current,
      goToStep,
      handleBack,
      handleNext,
      isFirst,
      isLast,
      isValidating,
      reset,
      total,
    ],
  );

  return useMemo(() => ({ ...form, steps }), [form, steps]);
};
